import React, { useState, useMemo } from 'react';

import styles from './styles.module.css';
import EmptyData from '../Form/EmptyData';
import ChevronIcon from '@/assets/icons/chevron.svg?react';

export interface DataTableColumn<T = any> {
  /** Unique field identifier */
  field?: keyof T;
  /** Display name for the column header */
  headerName: string;
  /** Column width in pixels, percentage, or 'auto' for flexible width */
  width?: number | 'auto' | string;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Whether the column content should be aligned to the left */
  alignLeft?: boolean;
  alignCenter?: boolean;
  /** Custom value getter function */
  valueGetter?: (value: any, row: T) => any;
  /** Custom cell renderer function */
  renderCell?: (params: {
    value: any;
    row: T;
    field: keyof T;
  }) => React.ReactNode;
  /** Column type for special formatting */
  type?: 'string' | 'number' | 'date';
  /** Custom row click handler */
  onClick?: (row: T) => void;
  needPadding?: boolean | ((row: T) => boolean);
}

export interface DataTableProps<T = any> {
  rows: T[];
  columns: DataTableColumn<T>[];
  initialState?: {
    sorting?: {
      field: keyof T;
      sort: 'asc' | 'desc';
    };
  };
  className?: string;
  title?: string;
  type?: 'admin' | 'detail';
  selectable?: boolean;
  selectedRows?: T[];
  onRowSelectionChange?: (selectedRows: T[]) => void;
  getRowId?: (row: T) => string | number;
  getRowStyle?: (row: T, rowIndex: number) => React.CSSProperties;
  noLeftMargin?: boolean;
  alignTextLeft?: boolean;
}

type SortConfig<T> = {
  field: keyof T;
  direction: 'asc' | 'desc';
} | null;

function DataTable<T extends Record<string, any>>({
  rows,
  columns,
  initialState,
  className,
  title = '',
  type = 'admin',
  selectable = false,
  selectedRows = [],
  onRowSelectionChange,
  getRowId = (row: T) => row.id || row.key || String(row),
  getRowStyle,
  noLeftMargin = false,
  alignTextLeft = false
}: DataTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>(
    initialState?.sorting
      ? {
          field: initialState.sorting.field,
          direction: initialState.sorting.sort
        }
      : null
  );

  const getColumnStyle = (column: DataTableColumn<T>) => {
    if (column.width === 'auto') {
      const autoColumns = columns.filter(col => col.width === 'auto').length;
      const fixedWidth = columns
        .filter(
          col =>
            col.width !== 'auto' && col.width && typeof col.width === 'number'
        )
        .reduce((sum, col) => sum + (col.width as number), 0);

      if (autoColumns === columns.length) {
        return { flex: 1 };
      }

      const remainingWidth = `calc((100% - ${fixedWidth}px) / ${autoColumns})`;
      return { width: remainingWidth, minWidth: '100px' };
    }

    // Handle percentage strings (e.g., '25%', '50%', etc.)
    if (typeof column.width === 'string' && column.width.includes('%')) {
      return { width: column.width };
    }

    return { width: column.width || 'auto' };
  };

  const handleSort = (field: keyof T) => {
    const column = columns.find(col => col.field === field);
    if (!column?.sortable) return;

    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.field === field &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }

    setSortConfig({ field, direction });
  };

  const handleRowClick = (row: T) => {
    if (!selectable || !onRowSelectionChange) return;

    const rowId = getRowId(row);
    const isSelected = selectedRows.some(
      selectedRow => getRowId(selectedRow) === rowId
    );

    let newSelectedRows: T[];
    if (isSelected) {
      // Remove from selection
      newSelectedRows = selectedRows.filter(
        selectedRow => getRowId(selectedRow) !== rowId
      );
    } else {
      // Add to selection
      newSelectedRows = [...selectedRows, row];
    }

    onRowSelectionChange(newSelectedRows);
  };

  const isRowSelected = (row: T) => {
    if (!selectable) return false;
    const rowId = getRowId(row);
    return selectedRows.some(selectedRow => getRowId(selectedRow) === rowId);
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;

    const sortColumn = columns.find(col => col.field === sortConfig.field);

    return [...rows].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn?.valueGetter) {
        const aFieldValue = a[sortConfig.field];
        const bFieldValue = b[sortConfig.field];
        aValue = sortColumn.valueGetter(aFieldValue, a);
        bValue = sortColumn.valueGetter(bFieldValue, b);
      } else {
        aValue = a[sortConfig.field];
        bValue = b[sortConfig.field];
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();

      if (sortConfig.direction === 'asc') {
        return aString.localeCompare(bString);
      } else {
        return bString.localeCompare(aString);
      }
    });
  }, [rows, sortConfig, columns]);

  const getCellValue = (row: T, column: DataTableColumn<T>) => {
    const fieldValue = row[column?.field || ''];
    const value = column.valueGetter
      ? column.valueGetter(fieldValue, row)
      : fieldValue;

    if (column.renderCell) {
      return column.renderCell({ value, row, field: column?.field || '' });
    }

    if (column.onClick) {
      return (
        <span
          className={styles.clickableCell}
          onClick={e => {
            e.stopPropagation();
            column.onClick!(row);
          }}
        >
          {value}
        </span>
      );
    }

    return value;
  };

  const getSortIconClass = (field: keyof T) => {
    if (!sortConfig || sortConfig.field !== field)
      return styles.sortIconDefault;
    return sortConfig.direction === 'asc'
      ? styles.sortIconUp
      : styles.sortIconDown;
  };

  const getDefaultRowStyle = (rowIndex: number) => {
    if (type === 'detail' && rowIndex % 2 === 1) {
      return { backgroundColor: 'var(--color-background-light-blue8)' };
    }
    return {};
  };

  return (
    <div
      className={`${styles.tableContainer} ${type === 'detail' ? styles.detailType : ''} ${className || ''}`}
      role="table"
    >
      <div className={styles.table}>
        {title && (
          <div className={styles.tableTitle}>
            <span>{title}</span>
          </div>
        )}
        {type === 'admin' && (
          <div className={styles.tableHeader} role="rowgroup">
            {columns.map(column => (
              <div
                key={String(column.field)}
                className={`${styles.headerCell} ${column.sortable ? styles.sortable : ''} ${column.alignLeft && !noLeftMargin ? styles.leftAligned : ''}`}
                style={{
                  ...getColumnStyle(column),
                  ...(column.alignLeft && noLeftMargin
                    ? { marginLeft: 0 }
                    : {}),
                  ...(column.alignCenter
                    ? { justifyContent: 'center', textAlign: 'center' }
                    : alignTextLeft
                      ? { justifyContent: 'flex-start', textAlign: 'left' }
                      : {})
                }}
                onClick={() =>
                  column.sortable && handleSort(column?.field || '')
                }
                role="columnheader"
                tabIndex={column.sortable ? 0 : -1}
                onKeyDown={e => {
                  if (column.sortable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleSort(column?.field || '');
                  }
                }}
                aria-sort={
                  sortConfig?.field === column?.field
                    ? sortConfig?.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : column.sortable
                      ? 'none'
                      : undefined
                }
              >
                <div>
                  <span className={styles.headerText}>{column.headerName}</span>
                  {column.sortable && (
                    <ChevronIcon
                      className={`${styles.sortIcon} ${getSortIconClass(column?.field || '')}`}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className={styles.tableBody} role="rowgroup">
          {sortedRows.length === 0 ? (
            <EmptyData label="No data available" />
          ) : (
            sortedRows.map((row, rowIndex) => (
              <div
                key={row.id || rowIndex}
                className={`${styles.tableRow} ${rowIndex % 2 === 0 ? styles.evenRow : styles.oddRow} ${
                  selectable ? styles.selectableRow : ''
                } ${isRowSelected(row) ? styles.selectedRow : ''}`}
                role="row"
                onClick={() => handleRowClick(row)}
                style={{
                  cursor: selectable ? 'pointer' : 'default',
                  ...getDefaultRowStyle(rowIndex),
                  ...(getRowStyle ? getRowStyle(row, rowIndex) : {})
                }}
              >
                {columns.map(column => {
                  const shouldHavePadding =
                    column.needPadding === undefined
                      ? true
                      : typeof column.needPadding === 'function'
                        ? column.needPadding(row)
                        : column.needPadding;

                  return (
                    <div
                      key={String(column.field)}
                      className={`${styles.tableCell} ${column.alignLeft && !noLeftMargin ? styles.leftAligned : ''}`}
                      style={{
                        ...getColumnStyle(column),
                        ...(column.alignLeft && noLeftMargin
                          ? { marginLeft: 0 }
                          : {}),
                        ...(column.alignCenter
                          ? { justifyContent: 'center', textAlign: 'center' }
                          : alignTextLeft
                            ? {
                                justifyContent: 'flex-start',
                                textAlign: 'left'
                              }
                            : {}),
                        ...(!shouldHavePadding ? { padding: 0 } : {})
                      }}
                      role="cell"
                    >
                      {getCellValue(row, column)}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default DataTable;
